import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import CustomModal from "../../packages/CustomModal";
import IconButton from "../../packages/IconButton";
import WPAgGrid from "../../packages/WPAgGrid/WPAgGrid";
import WPLoader from "../../packages/WPLoader";

import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import StatusApiErrorDialog from "../../components/StatusAPIErrorDialog";
import Charts from "../../FileUpload/chart";
import { ADVANCED_SETTINGS_SECTION_KEYS } from "../analyseData/AdvancedSettings/advancedSettings.constants";

import { STATUS_API_ERROR_KEYS } from "../../constants/global.constants";

import { QuestionIcon } from "../../assets/icons";

import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";

import {
  createCleaningEffectivenessCell,
  getCleaningEffectivenessFlags,
  getCleaningEffectivenessNumeric,
} from "../../utils/cleaningEffectivenessValue";
import { XlsNormalizefileKey } from "../../utils/constantsString";
import {
  dateFormatter,
  getSortedData,
  pressureFormatter,
  typeFormatter,
} from "../../utils/tableDataFormatter";
import {
  extractPlantId,
  getIndex,
  getPercentageLoaderMsg,
} from "../../utils/Utils";

import {
  cleaningEffectivness,
  cleaningEffectivnessStatus,
} from "../../network/cipAPI";

import {
  storeClenaingEffectiness,
  storeClenaingEffectinessThreshold,
} from "../../Store/TemplateStore/template.action";

import "./index.scss";

// Maps ag-grid field name to the translation key prefix for cell flag text
const FIELD_TO_KEY_PREFIX = {
  pressure_drop: "Dp",
  flow: "Pf",
  salt_passage: "Sp",
};

const FLAG_DISPLAY_ORDER = {
  R: 0,
  W: 1,
  E: 2,
};

// Returns display-ready flag codes in uppercase, comma-separated (e.g. "R, W").
const getFlagCodesText = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return null;
  const flags = getCleaningEffectivenessFlags(rawValue);
  if (!flags.length) return null;

  return flags
    .slice()
    .sort(
      (a, b) =>
        (FLAG_DISPLAY_ORDER[a] ?? Number.MAX_SAFE_INTEGER) -
        (FLAG_DISPLAY_ORDER[b] ?? Number.MAX_SAFE_INTEGER),
    )
    .join(", ");
};

// Renders comma-separated flag codes when present.
// Falls back to the formatted numeric value for unflagged cells.
const CleaningEffectivenessCellRenderer = (props) => {
  const rawValue = props.value;

  if (rawValue === null || rawValue === undefined) return <span>*</span>;

  const flagText = getFlagCodesText(rawValue);
  if (flagText) return <span>{flagText}</span>;

  return <span>{props.valueFormatted ?? ""}</span>;
};

const HistoricalCIPEffectivness = ({
  activeStep,
  setAdvancedSettingsConfig,
  setDisableNextButton,
}) => {
  const [rowData, setRowData] = useState(
    useSelector((state) => state.cleaningEffectiness),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [processedPercentage, setprocessedPercentage] = useState(false);
  const [threshold, setThreshold] = useState(null);
  const [isNoStablePointsDialogOpen, setIsNoStablePointsDialogOpen] =
    useState(false);

  const {
    cleanData: cleaningResponse,
    selTrain: selectedTrain,
    cleanDate: date,
    cips,
    foulingType,
  } = useSelector((state) => state);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const gridRef = useRef(null);

  // Shared tooltip getter for the three cleaning-effectiveness columns
  const cleaningEffTooltip = (params) =>
    getFlagCodesText(params.value) ?? params.valueFormatted ?? "";
  const intervalIdRef = useRef(null);
  const plantId = extractPlantId();

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    setAdvancedSettingsConfig?.({
      selectedTrain,
      enabledSections: [
        ADVANCED_SETTINGS_SECTION_KEYS.CLEANING_EFFECTIVENESS_CALCULATION,
      ],
      onSave: fetchCleaningEffectivenessData,
    });
    return () => setAdvancedSettingsConfig?.(null);
  }, [selectedTrain]);

  const reasonFormatter = (params) => {
    const { data } = params;
    const reason = data?.reason;
    if (
      !reason ||
      (Array.isArray(reason) && reason.length === 0) ||
      (typeof reason === "string" && !reason.trim())
    ) {
      return t("foulingStrings.noReason");
    }

    const typeArr = Array.isArray(reason)
      ? reason
          .filter(Boolean)
          .map((item) => String(item).trim())
          .filter(Boolean)
      : reason
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

    if (!typeArr.length) {
      return t("foulingStrings.noReason");
    }

    return typeArr.map((item) => foulingType?.[item] || item).join(", ");
  };

  const colDefs = [
    {
      headerName: "",
      children: [
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.index"),
          valueGetter: (params) => getIndex(params, params.data.start_date),
          flex: 0.5,
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.type"),
          field: "type",
          sortable: true,
          flex: 0.5,
          valueFormatter: (params) => typeFormatter(params, t),
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.start_date"),
          field: "start_date",
          sortable: true,
          flex: 0.75,
          valueFormatter: dateFormatter,
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.reason"),
          field: "reason",
          sortable: true,
          flex: 1.8,
          valueFormatter: reasonFormatter,
          valueGetter: reasonFormatter,
        },
      ],
    },
    {
      headerName: t("cleaningEffectivnessStrings.cleaningEffectiveness"),
      children: [
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.pressure_drop"),
          field: "pressure_drop",
          sortable: true,
          flex: 1,
          valueFormatter: pressureFormatter,
          useValueFormatterForExport: true,
          cellRenderer: CleaningEffectivenessCellRenderer,
          tooltipValueGetter: cleaningEffTooltip,
          headerClass: "left-divider",
          cellClass: "left-divider",
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.flow"),
          field: "flow",
          sortable: true,
          flex: 1,
          valueFormatter: pressureFormatter,
          useValueFormatterForExport: true,
          cellRenderer: CleaningEffectivenessCellRenderer,
          tooltipValueGetter: cleaningEffTooltip,
        },
        {
          headerName: t("HistoricalCIPEffectivnessTableStrings.salt_passage"),
          field: "salt_passage",
          sortable: true,
          flex: 1,
          valueFormatter: pressureFormatter,
          useValueFormatterForExport: true,
          cellRenderer: CleaningEffectivenessCellRenderer,
          tooltipValueGetter: cleaningEffTooltip,
        },
      ],
    },
  ];

  const completed_state = (responseData) => {
    const data = JSON.parse(responseData.result);
    const pressureDropFlagsMap = data.norm_pressure_drop_flag || {};
    const permeateFlowFlagsMap = data.norm_permeate_flow_flag || {};
    const saltPassageFlagsMap = data.norm_salt_passage_flag || {};

    const buildValueWithFlags = (value, flags) => {
      if (value === null || value === undefined) return null;
      return createCleaningEffectivenessCell(value, flags);
    };

    const result = Object.keys(data?.cip).map((key, index) => ({
      start_date: data.cip[index]["sd"],
      type: data.cip[index]["type"],
      pressure_drop: buildValueWithFlags(
        data[XlsNormalizefileKey.normalizeDPRack2][index],
        pressureDropFlagsMap[index],
      ),
      flow: buildValueWithFlags(
        data[XlsNormalizefileKey.normalizePermeateFlow][index],
        permeateFlowFlagsMap[index],
      ),
      salt_passage:
        buildValueWithFlags(
          data[XlsNormalizefileKey.normalizeSaltPassage]?.[index],
          saltPassageFlagsMap[index],
        ) ?? "-",
      reason: data.cip_reason[index]?.join(",  "),
    }));
    const sortedData = getSortedData(result);
    dispatch(storeClenaingEffectiness(sortedData));
    setRowData(sortedData);
    // fetch threshold data from result
    if (data?.threshold) {
      setThreshold(data?.threshold);
      dispatch(storeClenaingEffectinessThreshold(data?.threshold));
    }
    setIsLoading(false);
    setIsNoStablePointsDialogOpen(false);
    setDisableNextButton?.(false);
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  };

  const fetchCleaningEffectivenessData = async () => {
    try {
      setIsNoStablePointsDialogOpen(false);
      setDisableNextButton?.(false);
      setIsLoading(true);
      const res = await cleaningEffectivness(selectedTrain);
      if (res && res.data?.status === "completed") {
        completed_state(res?.data);
      } else {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }
        intervalIdRef.current = setInterval(function () {
          checkStatus(res.data?.task_id);
        }, 5000);
        setprocessedPercentage("1%");
        setIsLoading(true);
      }
    } catch (err) {
      setRowData([]);
      setIsLoading(false);
      setDisableNextButton?.(false);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.fetchFail"),
        "",
      );
    }
  };

  useEffect(() => {
    if (plantId) {
      fetchCleaningEffectivenessData();
    }

    // Cleanup function to clear interval when component unmounts
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, []);

  const checkStatus = async (task_id) => {
    try {
      const effectivenessStatus = await cleaningEffectivnessStatus(
        task_id,
        selectedTrain,
      );
      const data = effectivenessStatus?.data;
      const { status, processed, error } = data || {};

      const isNoStablePointsError =
        error === STATUS_API_ERROR_KEYS.NO_STABLE_POINTS;

      try {
        if (status === "completed") {
          completed_state(data);
        } else if (status === "pending") {
          setIsLoading(false);
          setDisableNextButton?.(false);
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }
          setprocessedPercentage("");
        } else if (status === "running") {
          setIsLoading(true);
          setDisableNextButton?.(false);
          setprocessedPercentage(processed);
        } else if (status === "failed") {
          setIsLoading(false);
          // setprocessedPercentage("");
          if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
          }

          if (isNoStablePointsError) {
            setDisableNextButton?.(true);
            setIsNoStablePointsDialogOpen(true);
          } else {
            setDisableNextButton?.(false);
            createNotification(
              ERROR_NOTIFICATION,
              error || t("notificationStrings.fetchFail"),
              "",
            );
          }

          setRowData([]);
        }
      } catch (error) {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        setRowData([]);
        setIsLoading(false);
        setDisableNextButton?.(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.fetchFail"),
          "",
        );
      }
    } catch (err) {
      if (err.code === "ERR_NETWORK" || err.response.status === 502) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.networkError"),
          "",
        );
      } else {
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        setRowData([]);
        setIsLoading(false);
        setDisableNextButton?.(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.fetchFail"),
          "",
        );
      }
    }
  };
  const hasInvalidData = rowData?.some((data) =>
    Object.keys(FIELD_TO_KEY_PREFIX).some(
      (field) =>
        data[field] === null ||
        isNaN(getCleaningEffectivenessNumeric(data[field])),
    ),
  );

  const hasValuesGreaterThan100 = rowData?.some((data) =>
    Object.keys(FIELD_TO_KEY_PREFIX).some(
      (field) => getCleaningEffectivenessNumeric(data[field]) > 100,
    ),
  );

  const hasValuesLessThan0 = rowData?.some((data) =>
    Object.keys(FIELD_TO_KEY_PREFIX).some((field) => {
      const n = getCleaningEffectivenessNumeric(data[field]);
      // Exclude -1 sentinel values used to indicate flagged in-range conditions
      return n < 0 && n !== -1;
    }),
  );

  const checkAnyFlag = (flag) =>
    rowData?.some((data) =>
      Object.keys(FIELD_TO_KEY_PREFIX).some((field) =>
        getCleaningEffectivenessFlags(data[field]).includes(flag),
      ),
    );

  const hasSomeR = checkAnyFlag("R");
  const hasSomeW = checkAnyFlag("W");
  const hasSomeE = checkAnyFlag("E");
  const hasRows = Array.isArray(rowData) && rowData.length > 0;

  const disclaimerList = useMemo(() => {
    const disclaimerRules = [
      [hasRows, "cleaningEffectivnessStrings.note"],
      [
        hasValuesGreaterThan100,
        "cleaningEffectivnessStrings.noteForValuesrGeaterThan100",
      ],
      [
        hasValuesLessThan0,
        "cleaningEffectivnessStrings.noteForValuesLessThan0",
      ],
      [hasInvalidData, "cleaningEffectivnessStrings.noteWith*"],
      [hasSomeE, "cleaningEffectivnessStrings.footnoteE"],
      [hasSomeR, "cleaningEffectivnessStrings.footnoteR"],
      [hasSomeW, "cleaningEffectivnessStrings.footnoteW"],
    ];

    return disclaimerRules
      .filter(([isVisible]) => Boolean(isVisible))
      .map(([, translationKey]) => t(translationKey));
  }, [
    hasRows,
    hasValuesGreaterThan100,
    hasValuesLessThan0,
    hasInvalidData,
    hasSomeR,
    hasSomeW,
    hasSomeE,
    t,
  ]);

  const handleCustomModalOpen = () => {
    setIsHelpDialogOpen(true);
  };

  const handleCustomModalClose = () => {
    setIsHelpDialogOpen(false);
  };

  return (
    <div>
      <div className="descriptionText marginTop10 marginBtm10">
        {t("cleaningEffectivnessStrings.cleaningDescription")}
      </div>

      {isLoading || !rowData ? (
        <WPLoader loadingMsg={getPercentageLoaderMsg(t, processedPercentage)} />
      ) : (
        <>
          <div className="chart">
            <Charts
              plantId={plantId}
              cleaningResponse={cleaningResponse}
              date={date}
              cips={cips}
              threshold={threshold}
              activeStep={activeStep}
            />
          </div>

          <div className="ro-card-wrapper">
            <div className="ro-card-header">
              <div className="info-section">
                <div className="teal-heading">
                  {t("cleaningEffectivnessStrings.cleaningEffectivenessValues")}
                </div>
                <IconWithTooltip
                  label={t(
                    "helpInformation.historicalCIPEffectivenessTableHelp",
                  )}
                />
              </div>
              <IconButton id="help-dialog-btn" onClick={handleCustomModalOpen}>
                <QuestionIcon />
              </IconButton>
            </div>

            <div className="ro-ag-table">
              <WPAgGrid
                gridRef={gridRef}
                columnDefs={colDefs}
                rowData={rowData}
                exportFileName={t(
                  "cleaningEffectivnessStrings.cleaningEffectivenessValues",
                )}
                disclaimerList={disclaimerList}
              />
            </div>

            <div>
              {disclaimerList.map((line) => (
                <div
                  key={line}
                  className="descriptionText marginTop10 marginBtm10"
                >
                  {line}
                </div>
              ))}
            </div>
          </div>

          <hr />

          <div className="ro-disclaimer">
            {t("disclaimer", { currentYear })}
          </div>
        </>
      )}

      {isHelpDialogOpen && (
        <CustomModal
          show={isHelpDialogOpen}
          header={t("helpInformation.cleaningEffectivenessHelp")}
          onModalClose={handleCustomModalClose}
          confirmBtn={t("ValueStrings.okay")}
          size="small"
          width="575px"
        >
          <div className="help-dialog-content">
            <div className="help-section">
              <ul className="help-list">
                <li className="help-list-item">
                  {t("helpInformation.cleaningEffectivenessMainText")}
                  <ul className="help-nested-list">
                    <li className="help-nested-list-item">
                      {t("helpInformation.cleaningEffectivenessReducePoints")}
                    </li>
                    <li className="help-nested-list-item">
                      {t("helpInformation.cleaningEffectivenessReduceDays")}
                    </li>
                  </ul>
                </li>
                <li className="help-list-item">
                  {t("helpInformation.cleaningEffectivenessIncreaseDays")}
                </li>
              </ul>
            </div>
          </div>
        </CustomModal>
      )}
      <StatusApiErrorDialog
        show={isNoStablePointsDialogOpen}
        onClose={() => setIsNoStablePointsDialogOpen(false)}
      />
    </div>
  );
};

export default HistoricalCIPEffectivness;
